import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef, type ReactNode, type Ref } from 'react'
import { Link, useParams } from 'react-router'

import {
  coordinationAvailabilityKey,
  emergencyHelpHospitalKey,
  fetchCoordinationAvailability,
  fetchEmergencyHelpHospital,
  sendEmergencyHelpEvent,
  type EmergencyHelpHospital,
} from '../../api/emergencyHelp'
import { Icon } from '../../components/Icon'
import { ApiError } from '../../lib/api'
import { amtcDialNumber, amtcDisplayNumber } from '../../lib/amtc'
import { directionsUrl } from '../../lib/hospitalProfile'
import { dialablePhone } from '../../lib/phone'
import {
  BackLink,
  Call112Button,
  EmergencyHelpShell,
  Inline112,
} from './chrome'
import { useArrivalFocus } from './focus'
import { hospitalsPath, requestPath } from './paths'

/**
 * Emergency Help, screen 3: a partner hospital's emergency details (M35-T06,
 * spec §C.1.3, ADR-0064).
 *
 * Every detail shows ONLY when T03 returns it, and T03 returns it only once
 * AMTC has verified it. An unverified field is not shown as "unknown"; it is
 * simply not there, and 112 stands in its place.
 *
 * - The call button dials the verified EMERGENCY line, never the hospital's
 *   general phone, and only if `dialablePhone` accepts it.
 * - `emergency_status` is what the hospital reports, labelled as such, and is
 *   left out when null (a partner with no bed row).
 * - A null capability is unknown and renders nothing, never "no".
 * - No bed counts: they are not checked in real time, and the capacity
 *   warning below would contradict them (spec §C.1.3).
 * - "Request AMTC help" (M36-T08) appears only while a coordinator is on
 *   duty. The availability read runs beside the hospital's, never in front of
 *   112: until it answers, and whenever it fails, the screen says coordination
 *   is not available.
 */

const STATUS_LABEL: Record<string, string> = {
  open: 'Open',
  divert: 'On divert (sending emergency patients elsewhere)',
  closed: 'Closed',
}

export function EmergencyHospitalDetail(): ReactNode {
  const { slug = '' } = useParams()
  const query = useQuery({
    queryKey: emergencyHelpHospitalKey(slug),
    queryFn: ({ signal }) => fetchEmergencyHelpHospital(slug, signal),
    // At most one retry (see screen 2), and none for an unknown hospital.
    retry: (count, err) =>
      !(err instanceof ApiError && err.status === 404) && count < 1,
    // Offline, show the failure (and its 112) rather than "Loading…" forever.
    networkMode: 'always',
  })
  const heading = useArrivalFocus<HTMLHeadingElement>(!query.isPending)

  // Once per hospital, when it has loaded (an unknown slug is not a view).
  const viewed = useRef<string | null>(null)
  const loaded = query.data?.slug
  useEffect(() => {
    if (!loaded || viewed.current === loaded) return
    viewed.current = loaded
    sendEmergencyHelpEvent({ kind: 'viewed_hospital', hospital_slug: loaded })
  }, [loaded])

  return (
    <EmergencyHelpShell step={3} title={query.data?.name ?? 'Hospital details'}>
      <BackLink to={hospitalsPath}>All partner hospitals</BackLink>

      {query.isPending && (
        <>
          <p role="status" className="text-[13px] text-ink-3">
            Loading hospital details…
          </p>
          <Call112Button />
        </>
      )}

      {query.isError && (
        <div role="alert" className="flex flex-col gap-3">
          <h1
            ref={heading}
            tabIndex={-1}
            className="font-display text-d-xs font-medium tracking-display text-ink-1 focus:outline-none"
          >
            {query.error instanceof ApiError && query.error.status === 404
              ? 'Hospital not found'
              : 'Could not load this hospital'}
          </h1>
          <p className="text-[13px] text-ink-2">
            In an emergency, do not wait. Call emergency services.
          </p>
          <Call112Button />
        </div>
      )}

      {query.isSuccess && (
        <Details hospital={query.data} headingRef={heading} />
      )}
    </EmergencyHelpShell>
  )
}

function Details({
  hospital: h,
  headingRef,
}: {
  hospital: EmergencyHelpHospital
  headingRef: Ref<HTMLHeadingElement>
}): ReactNode {
  const line = h.emergency_info_verified
    ? dialablePhone(h.emergency_phone)
    : null
  const amtc = amtcDialNumber()
  const amtcShown = amtcDisplayNumber()
  const canDirect =
    h.location_verified === true &&
    typeof h.lat === 'number' &&
    typeof h.lng === 'number'
  const status =
    h.emergency_status !== null
      ? (STATUS_LABEL[h.emergency_status] ?? h.emergency_status)
      : null

  const facts: string[] = []
  if (h.has_emergency === true) facts.push('Has an emergency department')
  if (h.has_emergency === false) facts.push('No emergency department')
  if (h.emergency_24h === true) facts.push('Emergency department open 24 hours')

  return (
    <>
      <div>
        <p className="font-mono text-[10px] uppercase tracking-wide text-ink-3">
          Approved hospital profile
        </p>
        <h1
          ref={headingRef}
          tabIndex={-1}
          className="mt-0.5 font-display text-d-xs font-medium tracking-display text-ink-1 focus:outline-none"
        >
          {h.name}
        </h1>
        <p className="mt-0.5 text-[12.5px] text-ink-3">
          {h.city} · AMTC partner
        </p>
      </div>

      <section
        aria-labelledby="eh-contact"
        className="rounded-r3 border border-line-1 px-4 py-3"
      >
        <h2 id="eh-contact" className="text-[14px] font-semibold text-ink-1">
          Emergency department contact
        </h2>
        {line ? (
          <>
            <p className="mt-1 text-[13px] tabular-nums text-ink-2">
              {h.emergency_phone}
            </p>
            <a
              href={`tel:${line}`}
              onClick={() =>
                sendEmergencyHelpEvent({
                  kind: 'called_hospital',
                  hospital_slug: h.slug,
                })
              }
              className="mt-2.5 flex items-center justify-center gap-2 rounded-r3 bg-accent px-4 py-3 text-[14px] font-semibold text-accent-on focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
            >
              <Icon name="phone" size={16} /> Call this hospital
            </a>
          </>
        ) : (
          <>
            <p className="mt-1 text-[13px] text-ink-2">
              {/* Verified, but not a number a phone can dial: say so, rather
                  than claim it was never checked. */}
              {h.emergency_info_verified && h.emergency_phone
                ? "This hospital's emergency line cannot be called from here."
                : 'Emergency line not yet verified.'}
            </p>
            <div className="mt-2.5">
              <Call112Button />
            </div>
          </>
        )}
        <p className="mt-2 text-[11.5px] text-ink-3">
          Calling does not notify AMTC automatically.
        </p>
      </section>

      <section aria-labelledby="eh-where" className="flex flex-col gap-2">
        <h2 id="eh-where" className="text-[14px] font-semibold text-ink-1">
          Address
        </h2>
        {h.address ? (
          <p className="text-[13px] text-ink-1">{h.address}</p>
        ) : (
          <p className="text-[13px] text-ink-2">
            Address not yet verified — call <Inline112 />
            {amtc ? (
              <>
                {' '}
                or the AMTC line,{' '}
                <a
                  href={`tel:${amtc}`}
                  className="font-semibold text-ink-1 underline underline-offset-2"
                >
                  {amtcShown ?? amtc}
                </a>
              </>
            ) : null}
            .
          </p>
        )}
        {canDirect && (
          <a
            href={directionsUrl(h.lat as number, h.lng as number)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() =>
              sendEmergencyHelpEvent({
                kind: 'got_directions',
                hospital_slug: h.slug,
              })
            }
            className="inline-flex items-center gap-1.5 self-start rounded-r3 border border-line-2 px-3 py-2 text-[13px] font-medium text-ink-1 hover:bg-bg-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            <Icon name="nav" size={14} /> Get directions
          </a>
        )}
      </section>

      {(facts.length > 0 || h.emergency_hours_text) && (
        <section aria-labelledby="eh-facts" className="flex flex-col gap-1.5">
          <h2 id="eh-facts" className="text-[14px] font-semibold text-ink-1">
            Emergency services
          </h2>
          <ul className="flex flex-col gap-1 text-[13px] text-ink-1">
            {facts.map((f) => (
              <li key={f}>{f}</li>
            ))}
            {h.emergency_hours_text && <li>Hours: {h.emergency_hours_text}</li>}
          </ul>
        </section>
      )}

      {/* Reported by the hospital, NOT checked by AMTC: kept apart from the
          verified facts above, so it is never read as one of them. */}
      {status && (
        <p className="text-[13px] text-ink-1">
          Emergency status: {status}{' '}
          <span className="text-ink-3">(as reported by the hospital)</span>
        </p>
      )}

      {h.last_checked_at && (
        <p className="text-[12px] text-ink-3">
          Profile last checked: {formatDate(h.last_checked_at)}
        </p>
      )}

      <p
        role="note"
        className="rounded-r3 border border-bed-warn/40 bg-bed-warn-bg px-3 py-2.5 text-[12.5px] leading-relaxed text-ink-1"
      >
        <span className="font-semibold">Capacity is not confirmed.</span> Call
        the hospital to confirm it can receive the patient. No bed is reserved.
      </p>

      <RequestAmtcHelp slug={h.slug} />
    </>
  )
}

/** Shown only while a coordinator is on duty (ADR-0065 decision 2). */
function RequestAmtcHelp({ slug }: { slug: string }): ReactNode {
  const available = useQuery({
    queryKey: coordinationAvailabilityKey,
    queryFn: ({ signal }) => fetchCoordinationAvailability(signal),
    retry: false,
    networkMode: 'always',
    // A switch can go off at any moment: never trust an old yes.
    staleTime: 0,
  })
  if (available.data === true) {
    return (
      <Link
        to={requestPath(slug)}
        className="flex items-center justify-center gap-2 rounded-r3 bg-ink-1 px-4 py-3 text-[14px] font-semibold text-bg-app focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink-1"
      >
        Request AMTC help
      </Link>
    )
  }
  return (
    <p className="text-[12.5px] text-ink-2">
      AMTC coordination is not available right now. In an emergency, call{' '}
      <Inline112 />.
    </p>
  )
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}
